/**
 * Responsibility: every grade's price, movement and liquidity, in one block.
 *
 * Shared by the discovery dialog and the search dialog because they are
 * answering the same question about the same data - both now read grades
 * from PriceCharting - and two copies would drift.
 *
 * The ladder matters more than its top rung. A card whose 9 and 10 sit close
 * together is a poor grading bet however large the 10 is, and one where the
 * 10 is fifty times the 9 is worth the risk on a much smaller card. Chansey
 * from XY Evolutions is the case in point: $21 at PSA 9 and $1,549 at PSA 10.
 *
 * "Trades" is the catalogue's own words for how often one changes hands, and
 * is the nearest thing to a scarcity signal available outside Pokemon's
 * population data. A PSA 10 worth $1,549 that trades twice a year is not
 * $1,549 you can realise on demand, and that is the number most likely to
 * kill an attractive-looking row.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { GradeQuote } from '../../api/client';
import { money, toNumber } from '../grading/money';
import { EM_DASH } from '../grading/CardFigures';

type GradeLadderProps = { grades: GradeQuote[]; dense?: boolean };

export function GradeLadder({ grades, dense = false }: GradeLadderProps) {
  if (grades.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No per-grade prices for this card.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(64px, auto) 1fr auto',
        columnGap: 1.5,
        rowGap: dense ? 0.25 : 0.5,
        alignItems: 'center',
      }}
    >
      {grades.map((grade) => {
        const change = toNumber(grade.change);
        return (
          <Box key={grade.label} sx={{ display: 'contents' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {grade.label}
            </Typography>

            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                color={grade.price === null ? 'text.disabled' : 'text.primary'}
              >
                {grade.price === null ? EM_DASH : money(grade.price)}
              </Typography>
              {change !== null ? (
                <Typography
                  variant="caption"
                  color={change >= 0 ? 'success.main' : 'error.main'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {change >= 0 ? '+' : ''}
                  {money(grade.change)}
                </Typography>
              ) : null}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {grade.volume ?? 'no sales'}
            </Typography>

          </Box>
        );
      })}
    </Box>
  );
}
